import { RowSelectionState, SortingState } from '@tanstack/react-table';
import type { SourcingMetricsFilterRange } from 'api-clients/monolith';
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import { useLocalStorageState } from '@/hooks/use-local-storage-state';

export type Pagination = {
  pageIndex: number;
  pageSize: number;
};

export type Filters = {
  locationIds: string[];
  positionIds: string[];
  isSelectedForSourcing?: boolean;
  brandIds: string[];
  active: boolean | undefined;
  channelIds: string[];
  statuses: string[];
};

// A preset (e.g. "7"/"30" days, or the app-only "lifetime" sentinel) takes
// precedence over an explicit start/end pair when both are present.
export type DateRangeFilter = {
  preset?: SourcingMetricsFilterRange;
  startDate?: string;
  endDate?: string;
};

// Partial next-state for `resetQueryState`. Only provided keys are written —
// use `'searchQuery' in next` (not truthiness) so clearing search to
// `undefined` is distinguishable from leaving search alone.
export type ResetQueryStateInput = {
  filters?: Filters;
  searchQuery?: string | undefined;
  sorting?: SortingState;
  dateRange?: DateRangeFilter;
};

export type TableContextType = {
  pagination: Pagination;
  setPagination: Dispatch<SetStateAction<Pagination>>;
  rowCount: number;
  setRowCount: Dispatch<SetStateAction<number>>;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
  searchQuery: string | undefined;
  setSearchQuery: Dispatch<SetStateAction<string | undefined>>;
  filters: Filters;
  setFilters: Dispatch<SetStateAction<Filters>>;
  dateRange: DateRangeFilter;
  setDateRange: Dispatch<SetStateAction<DateRangeFilter>>;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  // Atomically update multiple query-backed slices in one `setSearchParams`
  // call. Prefer this over calling setFilters + setSearchQuery + setSorting
  // (+ setDateRange) in the same handler — those don't compose (see
  // `applyPageIndexParam`'s comment), so Clear can leave stale URL params.
  resetQueryState: (next: ResetQueryStateInput) => void;
};

export const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
export const DEFAULT_ROWS_PER_PAGE = ROWS_PER_PAGE_OPTIONS[0];

const defaultPagination = {
  pageIndex: 0,
  pageSize: DEFAULT_ROWS_PER_PAGE,
};

const defaultFilters: Filters = {
  locationIds: [],
  positionIds: [],
  // Openings page default: "Not added". Dashboard/campaigns don't surface this filter.
  isSelectedForSourcing: false,
  brandIds: [],
  active: undefined,
  channelIds: [],
  statuses: [],
};

const defaultDateRange: DateRangeFilter = {};

// Filter keys whose value is a `string[]` (comma-joined in the URL) rather
// than a single boolean, so `filters` query params only cover this subset.
type ListFilterKey =
  | 'locationIds'
  | 'positionIds'
  | 'brandIds'
  | 'channelIds'
  | 'statuses';

// Opt-in: table-specific URL search param names for each piece of state a
// table wants mirrored to/from the URL. Omitting a key (or the whole config)
// keeps that piece of state in-memory only, matching today's behavior.
export type TableQueryParamsConfig = {
  page?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  filters?: Partial<Record<ListFilterKey, string>>;
  dateRange?: {
    preset?: string;
    startDate?: string;
    endDate?: string;
  };
};

// URL page numbers are 1-indexed (user-facing); `pageIndex` is 0-indexed.
function parsePageIndexParam(value: string | null): number {
  const pageNumber = Number(value);

  return Number.isInteger(pageNumber) && pageNumber > 0
    ? pageNumber - 1
    : defaultPagination.pageIndex;
}

function parseListParam(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map(id => id.trim())
    .filter(Boolean);
}

// Sets `key=value` on `params`, or removes `key` when `value` is empty/falsy,
// so the URL never accumulates redundant/empty params.
function setOrDeleteParam(
  params: URLSearchParams,
  key: string,
  value: string | undefined | null,
) {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
}

// Applies a pageIndex change to `params` in place. Callers that also change
// another param (search/filters) in the same update MUST fold this into
// their own `setSearchParams` call rather than issuing a second one:
// `useSearchParams`'s updater always receives the same pre-render snapshot,
// so two separate `setSearchParams` calls in one event handler don't compose
// — the later call's `prev` doesn't see the earlier call's change, so it
// silently reintroduces the stale param (e.g. an old `page=3` surviving a
// search that should have reset to page 1).
function applyPageIndexParam(
  params: URLSearchParams,
  pageParam: string | undefined,
  nextPageIndex: number,
) {
  if (!pageParam) return;
  // Keep the URL clean on the first page rather than a redundant `page=1`.
  setOrDeleteParam(
    params,
    pageParam,
    nextPageIndex > 0 ? String(nextPageIndex + 1) : undefined,
  );
}

export const TableContext = createContext<TableContextType>({
  pagination: defaultPagination,
  setPagination: () => void 0,
  rowCount: 0,
  setRowCount: () => void 0,
  rowSelection: {},
  setRowSelection: () => void 0,
  searchQuery: undefined,
  setSearchQuery: () => void 0,
  filters: defaultFilters,
  setFilters: () => void 0,
  dateRange: defaultDateRange,
  setDateRange: () => void 0,
  sorting: [],
  setSorting: () => void 0,
  resetQueryState: () => void 0,
});

export function useTableContext() {
  const context = useContext(TableContext);

  if (!context)
    throw new Error('useTableContext must be used within a TableProvider');

  return context;
}

type TableProviderProps = PropsWithChildren<{
  // Opt-in: pass a unique key to persist the user's preferred page size in
  // localStorage so it survives a remount/session. Each table that wants
  // persistence should supply its own key (e.g. `source.dashboard.rowsPerPage`)
  pageSizeStorageKey?: string;
  initialRowSelection?: RowSelectionState;
  // Opt-in: pass URL search param names for the pieces of state a table wants
  // mirrored to/from the URL (page, search, sort, filters). This makes the
  // browser's back button return to the state the user was on — e.g. after
  // drilling into an opening and hitting back, they land back on the same
  // page/sort/filters/search — since that state lives on the history entry
  // the browser is restoring, rather than in memory (which a route change
  // would otherwise reset). Updates replace the current history entry so
  // paging/sorting/filtering around doesn't itself pile up back-button stops.
  // Omitting a key (or the whole config) keeps that piece of state in-memory
  // only, matching pre-existing behavior.
  queryParams?: TableQueryParamsConfig;
}>;

export function TableProvider({
  children,
  pageSizeStorageKey,
  queryParams,
  initialRowSelection,
}: TableProviderProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    page: pageParam,
    search: searchParam,
    sortBy: sortByParam,
    sortDirection: sortDirectionParam,
    filters: filterParams,
    dateRange: dateRangeParams,
  } = queryParams ?? {};

  const [pageIndex, setPageIndexState] = useState(() =>
    pageParam
      ? parsePageIndexParam(searchParams.get(pageParam))
      : defaultPagination.pageIndex,
  );

  const setPageIndex = useCallback(
    (nextPageIndex: number) => {
      setPageIndexState(nextPageIndex);

      if (!pageParam) return;

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          applyPageIndexParam(next, pageParam, nextPageIndex);
          return next;
        },
        { replace: true },
      );
    },
    [pageParam, setSearchParams],
  );

  const [storedPageSize, setStoredPageSize] = useLocalStorageState<number>(
    pageSizeStorageKey ?? null,
    DEFAULT_ROWS_PER_PAGE,
  );
  // Guard against a corrupted/stale localStorage value that no longer
  // matches one of the selectable options.
  const pageSize = ROWS_PER_PAGE_OPTIONS.includes(storedPageSize)
    ? storedPageSize
    : DEFAULT_ROWS_PER_PAGE;

  const pagination = useMemo<Pagination>(
    () => ({ pageIndex, pageSize }),
    [pageIndex, pageSize],
  );

  const setPagination: Dispatch<SetStateAction<Pagination>> = updater => {
    const next =
      typeof updater === 'function'
        ? updater({ pageIndex, pageSize })
        : updater;

    if (next.pageIndex !== pageIndex) setPageIndex(next.pageIndex);
    if (next.pageSize !== pageSize) setStoredPageSize(next.pageSize);
  };

  const [rowCount, setRowCount] = useState(0);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    initialRowSelection ?? {},
  );

  const [searchQuery, setSearchQueryState] = useState<string | undefined>(
    () =>
      (searchParam ? searchParams.get(searchParam) : undefined) ?? undefined,
  );
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;

  // Reset to the first page whenever the search query changes, so a new
  // search never leaves the user stranded on a page beyond the new result set.
  const setSearchQuery: Dispatch<SetStateAction<string | undefined>> =
    useCallback(
      updater => {
        const nextQuery =
          typeof updater === 'function'
            ? (updater as (prev: string | undefined) => string | undefined)(
                searchQueryRef.current,
              )
            : updater;

        searchQueryRef.current = nextQuery;
        setSearchQueryState(nextQuery);
        setPageIndexState(defaultPagination.pageIndex);

        if (pageParam || searchParam) {
          // Reset-to-page-1 and the search param write must land in the same
          // `setSearchParams` call — see `applyPageIndexParam`'s comment.
          setSearchParams(
            prev => {
              const next = new URLSearchParams(prev);
              applyPageIndexParam(next, pageParam, defaultPagination.pageIndex);
              if (searchParam) {
                setOrDeleteParam(next, searchParam, nextQuery || undefined);
              }
              return next;
            },
            { replace: true },
          );
        }
      },
      [pageParam, searchParam, setSearchParams],
    );

  const [filters, setFiltersState] = useState<Filters>(() => {
    if (!filterParams) return defaultFilters;

    const initialFilters = { ...defaultFilters };
    (Object.keys(filterParams) as ListFilterKey[]).forEach(key => {
      const paramName = filterParams[key];
      if (!paramName) return;

      const value = searchParams.get(paramName);
      if (value) initialFilters[key] = parseListParam(value);
    });

    return initialFilters;
  });
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Reset to the first page whenever filters change, so a new filter never
  // leaves the user stranded on a page beyond the new result set.
  const setFilters: Dispatch<SetStateAction<Filters>> = useCallback(
    updater => {
      const nextFilters =
        typeof updater === 'function'
          ? (updater as (prev: Filters) => Filters)(filtersRef.current)
          : updater;

      filtersRef.current = nextFilters;
      setFiltersState(nextFilters);
      setPageIndexState(defaultPagination.pageIndex);

      if (pageParam || filterParams) {
        // Reset-to-page-1 and the filter param writes must land in the same
        // `setSearchParams` call — see `applyPageIndexParam`'s comment.
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            applyPageIndexParam(next, pageParam, defaultPagination.pageIndex);

            if (filterParams) {
              (Object.keys(filterParams) as ListFilterKey[]).forEach(key => {
                const paramName = filterParams[key];
                if (!paramName) return;

                const value = nextFilters[key];
                setOrDeleteParam(
                  next,
                  paramName,
                  value.length ? value.join(',') : undefined,
                );
              });
            }
            return next;
          },
          { replace: true },
        );
      }
    },
    [pageParam, filterParams, setSearchParams],
  );

  const [dateRange, setDateRangeState] = useState<DateRangeFilter>(() => {
    if (!dateRangeParams) return defaultDateRange;

    const preset = dateRangeParams.preset
      ? (searchParams.get(dateRangeParams.preset) ?? undefined)
      : undefined;
    const startDate = dateRangeParams.startDate
      ? (searchParams.get(dateRangeParams.startDate) ?? undefined)
      : undefined;
    const endDate = dateRangeParams.endDate
      ? (searchParams.get(dateRangeParams.endDate) ?? undefined)
      : undefined;

    return {
      preset: preset as DateRangeFilter['preset'],
      startDate,
      endDate,
    };
  });
  const dateRangeRef = useRef(dateRange);
  dateRangeRef.current = dateRange;

  // Reset to the first page whenever the date range changes, so a new
  // range never leaves the user stranded on a page beyond the new result set.
  const setDateRange: Dispatch<SetStateAction<DateRangeFilter>> = useCallback(
    updater => {
      const nextDateRange =
        typeof updater === 'function'
          ? (updater as (prev: DateRangeFilter) => DateRangeFilter)(
              dateRangeRef.current,
            )
          : updater;

      dateRangeRef.current = nextDateRange;
      setDateRangeState(nextDateRange);
      setPageIndexState(defaultPagination.pageIndex);

      if (pageParam || dateRangeParams) {
        // Reset-to-page-1 and the date-range param writes must land in the
        // same `setSearchParams` call — see `applyPageIndexParam`'s comment.
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            applyPageIndexParam(next, pageParam, defaultPagination.pageIndex);

            if (dateRangeParams?.preset) {
              setOrDeleteParam(
                next,
                dateRangeParams.preset,
                nextDateRange.preset,
              );
            }
            if (dateRangeParams?.startDate) {
              setOrDeleteParam(
                next,
                dateRangeParams.startDate,
                nextDateRange.startDate,
              );
            }
            if (dateRangeParams?.endDate) {
              setOrDeleteParam(
                next,
                dateRangeParams.endDate,
                nextDateRange.endDate,
              );
            }
            return next;
          },
          { replace: true },
        );
      }
    },
    [pageParam, dateRangeParams, setSearchParams],
  );

  const [sorting, setSortingState] = useState<SortingState>(() => {
    if (!sortByParam) return [];

    const sortBy = searchParams.get(sortByParam);
    if (!sortBy) return [];

    const sortDirection = sortDirectionParam
      ? searchParams.get(sortDirectionParam)
      : null;

    return [{ id: sortBy, desc: sortDirection === 'desc' }];
  });
  const sortingRef = useRef(sorting);
  sortingRef.current = sorting;

  // Unlike search/filters, sorting doesn't reset the page — the result set
  // size is unchanged, only its order, so the user's current page still
  // points at valid rows.
  const setSorting: Dispatch<SetStateAction<SortingState>> = useCallback(
    updater => {
      const nextSorting =
        typeof updater === 'function'
          ? (updater as (prev: SortingState) => SortingState)(
              sortingRef.current,
            )
          : updater;

      sortingRef.current = nextSorting;
      setSortingState(nextSorting);

      if (sortByParam || sortDirectionParam) {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            const sort = nextSorting[0];

            if (sortByParam) setOrDeleteParam(next, sortByParam, sort?.id);
            if (sortDirectionParam) {
              setOrDeleteParam(
                next,
                sortDirectionParam,
                sort ? (sort.desc ? 'desc' : 'asc') : undefined,
              );
            }
            return next;
          },
          { replace: true },
        );
      }
    },
    [sortByParam, sortDirectionParam, setSearchParams],
  );

  const resetQueryState = useCallback(
    (next: ResetQueryStateInput) => {
      const hasFilters = 'filters' in next;
      const hasSearch = 'searchQuery' in next;
      const hasSorting = 'sorting' in next;
      const hasDateRange = 'dateRange' in next;

      const nextFilters = hasFilters ? next.filters! : filtersRef.current;
      const nextSearch = hasSearch ? next.searchQuery : searchQueryRef.current;
      const nextSorting = hasSorting ? next.sorting! : sortingRef.current;
      const nextDateRange = hasDateRange
        ? next.dateRange!
        : dateRangeRef.current;

      if (hasFilters) {
        filtersRef.current = nextFilters;
        setFiltersState(nextFilters);
      }
      if (hasSearch) {
        searchQueryRef.current = nextSearch;
        setSearchQueryState(nextSearch);
      }
      if (hasSorting) {
        sortingRef.current = nextSorting;
        setSortingState(nextSorting);
      }
      if (hasDateRange) {
        dateRangeRef.current = nextDateRange;
        setDateRangeState(nextDateRange);
      }

      // Search/filters/date-range change the result set; reset to page 1.
      // Sorting alone does not — match setSorting's behavior.
      const resetsPage = hasFilters || hasSearch || hasDateRange;
      if (resetsPage) {
        setPageIndexState(defaultPagination.pageIndex);
      }

      const shouldSyncUrl = Boolean(
        pageParam ||
        searchParam ||
        filterParams ||
        dateRangeParams ||
        sortByParam ||
        sortDirectionParam,
      );
      if (!shouldSyncUrl) return;

      // One setSearchParams for every provided slice — calling the individual
      // setters in the same handler would race (see applyPageIndexParam).
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);

          if (resetsPage) {
            applyPageIndexParam(params, pageParam, defaultPagination.pageIndex);
          }

          if (hasSearch && searchParam) {
            setOrDeleteParam(params, searchParam, nextSearch || undefined);
          }

          if (hasFilters && filterParams) {
            (Object.keys(filterParams) as ListFilterKey[]).forEach(key => {
              const paramName = filterParams[key];
              if (!paramName) return;

              const value = nextFilters[key];
              setOrDeleteParam(
                params,
                paramName,
                value.length ? value.join(',') : undefined,
              );
            });
          }

          if (hasDateRange && dateRangeParams) {
            if (dateRangeParams.preset) {
              setOrDeleteParam(
                params,
                dateRangeParams.preset,
                nextDateRange.preset,
              );
            }
            if (dateRangeParams.startDate) {
              setOrDeleteParam(
                params,
                dateRangeParams.startDate,
                nextDateRange.startDate,
              );
            }
            if (dateRangeParams.endDate) {
              setOrDeleteParam(
                params,
                dateRangeParams.endDate,
                nextDateRange.endDate,
              );
            }
          }

          if (hasSorting && (sortByParam || sortDirectionParam)) {
            const sort = nextSorting[0];
            if (sortByParam) setOrDeleteParam(params, sortByParam, sort?.id);
            if (sortDirectionParam) {
              setOrDeleteParam(
                params,
                sortDirectionParam,
                sort ? (sort.desc ? 'desc' : 'asc') : undefined,
              );
            }
          }

          return params;
        },
        { replace: true },
      );
    },
    [
      pageParam,
      searchParam,
      filterParams,
      dateRangeParams,
      sortByParam,
      sortDirectionParam,
      setSearchParams,
    ],
  );

  return (
    <TableContext.Provider
      value={{
        pagination,
        setPagination,
        rowCount,
        setRowCount,
        rowSelection,
        setRowSelection,
        searchQuery,
        setSearchQuery,
        filters,
        setFilters,
        dateRange,
        setDateRange,
        sorting,
        setSorting,
        resetQueryState,
      }}
    >
      {children}
    </TableContext.Provider>
  );
}
